import { ArrowLeft, Expand, Maximize2, Minimize, Minimize2, Copy, Download, Save, Sparkles } from "lucide-react";
import React, { useState } from "react";
import { Button, Select, SelectItem, Avatar, Skeleton, Autocomplete, AutocompleteItem, Tooltip, Input, Textarea, Card, CardBody, CardHeader } from "@heroui/react";
import { BrandVoiceSelector } from "@/components/brand-voice/brand-voice-selector";
import { EmotionSelector } from "@/components/emotion/emotion-selector";
import { motion, AnimatePresence } from "framer-motion";
import { Id, Doc } from "@/convex/_generated/dataModel";
import { contentFrameworks } from "@/lib/constants/content-frameworks";
import { platformEmotions } from "@/lib/constants/platform-emotions";
import { platformBrandVoices } from "@/lib/constants/platform-voices";
import { TEMPLATE } from "@/app/app/(data)/Templates";
import { useIsMounted } from "usehooks-ts";
import { GlowEffect } from "@/components/global-ui/glow-effect";

// Define reading levels directly to avoid circular dependency
const dummyReadingLevels = [{
  value: "grade5",
  label: "5th Grade",
  description: "Clear and simple, easily understood by most readers"
}, {
  value: "grade8",
  label: "8th Grade",
  description: "Balanced readability, ideal for general business content"
}, {
  value: "grade10",
  label: "10th Grade",
  description: "More sophisticated language, good for professional content"
}, {
  value: "grade12",
  label: "12th Grade",
  description: "Advanced vocabulary, suitable for academic or technical content"
}, {
  value: "college",
  label: "College Level",
  description: "Complex concepts and vocabulary for specialized audiences"
}];

// Dummy data for preview
const dummyPersonas = [{
  _id: "persona1" as Id<"personas">,
  _creationTime: Date.now(),
  orgId: "org1",
  name: "Marketing Manager",
  job_title: "Digital Marketing Manager",
  challenges: ["Limited budget for campaigns", "Need to prove ROI"],
  painPoints: ["Struggles with content creation efficiency"],
  demographics: "25-35, Tech-savvy professional",
  goals: "Increase brand awareness and lead generation",
  behaviors: "Active on social media, data-driven decisions",
  siteId: "site1" as Id<"sites">
}, {
  _id: "persona2" as Id<"personas">,
  _creationTime: Date.now(),
  orgId: "org1",
  name: "Startup Founder",
  job_title: "CEO & Founder",
  challenges: ["Building brand from scratch", "Limited marketing team"],
  painPoints: ["Needs viral content to gain traction"],
  demographics: "28-40, Entrepreneur",
  goals: "Build product awareness and user base",
  behaviors: "Networking, content creation, growth hacking",
  siteId: "site1" as Id<"sites">
}, {
  _id: "persona3" as Id<"personas">,
  _creationTime: Date.now(),
  orgId: "org1",
  name: "Content Creator",
  job_title: "Social Media Content Creator",
  challenges: ["Consistent content creation", "Standing out in crowded space"],
  painPoints: ["Running out of creative ideas"],
  demographics: "22-30, Creative professional",
  goals: "Grow follower base and engagement",
  behaviors: "Trend watching, community building",
  siteId: "site1" as Id<"sites">
}];
const dummyPainPoints = [{
  _id: "pain1" as Id<"painPoints">,
  _creationTime: Date.now(),
  orgId: "org1",
  shortName: "Content Creation Burnout",
  fullDescription: "Struggling to consistently create engaging content that resonates with target audience",
  personaId: "persona1" as Id<"personas">,
  siteId: "site1" as Id<"sites">
}, {
  _id: "pain2" as Id<"painPoints">,
  _creationTime: Date.now(),
  orgId: "org1",
  shortName: "Low Social Media Engagement",
  fullDescription: "Posts are not getting the reach and engagement needed to grow the business",
  personaId: "persona1" as Id<"personas">,
  siteId: "site1" as Id<"sites">
}, {
  _id: "pain3" as Id<"painPoints">,
  _creationTime: Date.now(),
  orgId: "org1",
  shortName: "Brand Awareness Challenge",
  fullDescription: "Difficulty building brand recognition in a competitive market",
  personaId: "persona2" as Id<"personas">,
  siteId: "site1" as Id<"sites">
}];
const dummyCustomBrandVoices = [{
  label: "Friendly & Approachable",
  value: "custom-friendly",
  description: "Warm, conversational tone that builds trust"
}, {
  label: "Expert Authority",
  value: "custom-expert",
  description: "Professional, knowledgeable voice that establishes credibility"
}];
const dummyTemplate: TEMPLATE = {
  name: "Twitter Thread Generator",
  desc: "Create viral Twitter threads that engage your audience",
  category: "Social Media",
  platform: "Twitter" as const,
  icon: {
    type: "platform" as const,
    icon: "twitter"
  },
  slug: "twitter-thread",
  enableCtaLink: true,
  form: [{
    label: "Thread Topic",
    field: "input" as const,
    name: "topic",
    required: true,
    placeholder: "What's your thread about?"
  }, {
    label: "Key Points",
    field: "textarea" as const,
    name: "keyPoints",
    required: true,
    placeholder: "List the main points you want to cover..."
  }]
};
const dummyProjectData = {
  _id: "project1" as Id<"projects">,
  _creationTime: Date.now(),
  orgId: "org1",
  userId: "user1",
  name: "SaaS Marketing Project",
  preferredEmotion: "AHA (💡 Educational)",
  preferredBrandVoice: "professional-friendly",
  preferredReadingLevel: "high-school",
  preferredPronounStyle: "singular",
  preferredContentFramework: "aida",
  websiteSummary: "A productivity SaaS tool that helps teams collaborate better",
  siteId: "site1" as Id<"sites">,
  createdAt: new Date().toISOString(),
  updatedAt: new Date().toISOString()
};
const dummyAiOutput = `🚀 How I Built a $10K MRR SaaS in 6 Months (Thread) 🧵

1/ Getting traction seemed impossible until I discovered this framework. Here's exactly how I went from idea to $10K MRR in just 6 months 👇

2/ The Problem: Most entrepreneurs fail because they build products nobody wants. I spent weeks researching pain points in the productivity space before writing a single line of code.

3/ The Solution: I built a simple tool that solved ONE specific problem really well - team communication overload. No feature bloat, just pure value.

4/ Validation First: Before building, I created a landing page and got 200 email signups. This told me people actually wanted what I was building.

5/ MVP in 30 Days: I used no-code tools to build the first version quickly. Speed > Perfection in the early days.

6/ First Customers: I personally reached out to my email list and onboarded the first 10 customers manually. This taught me what they really needed.

7/ Product-Market Fit: When customers started referring friends without me asking, I knew I had something special.

8/ Growth Strategy: I focused on content marketing and SEO. Wrote 2 blog posts per week sharing our journey and lessons learned.

9/ The Numbers:
- Month 1: $0 
- Month 2: $500
- Month 3: $1,200
- Month 4: $3,500
- Month 5: $7,200
- Month 6: $10,400

10/ Key Lessons:
- Start with the problem, not the solution
- Talk to customers daily
- Ship fast, improve faster
- Content is king for SaaS growth

What questions do you have about building a SaaS? Drop them below! 👇

#SaaS #Startup #Entrepreneur #BuildInPublic`;

// Define specific types for props with minimal required props for dummy component
interface DummyTemplateContentGeneratorProps {
  shouldShowBackButton: boolean;
  shouldShowExpandButton: boolean;
  isBothOptionsEnabled: boolean;
  showBrandVoiceOnParent: boolean;
  showContentFrameworkOnParent: boolean;
  predefinedBrandVoices: Array<{
    label: string;
    value: string;
    description?: string;
  }>;
  customBrandVoices: Array<{
    label: string;
    value: string;
    description?: string;
  }>;
  predefinedEmotions: Array<{
    label: string;
    value: string;
    description: string;
  }>;
  selectedEmotion: string | null;
  personas?: Array<{
    _id: Id<"personas">;
    name: string;
    job_title: string;
  }>;
  painPoints?: Array<{
    _id: Id<"painPoints">;
    shortName: string;
    fullDescription: string;
  }>;
  selectedPainPointId: Id<"painPoints"> | null;
  selectedContentFramework: string | null;
  selectedBrandVoice: string;
  selectedReadingLevel: string | null;
  selectedPronounStyle: string | null;
  handlePersonaChange: (value: string) => void;
  handleEmotionChange: (value: string | null) => Promise<void>;
  handleBrandVoiceChange: (value: string | null) => Promise<void>;
  handleReadingLevelChange: (value: string) => Promise<void>;
  handleWebsiteSummaryChange: (value: string) => Promise<void>;
  handlePronounStyleChange: (value: string) => Promise<void>;
  handleContentFrameworkChange: (value: string) => Promise<void>;
  GenerateAIContent: (formData: any) => Promise<void>;
  loading: boolean;
  isSaving: boolean;
  handleSaveOutput: (manualContent?: string) => Promise<void>;
  handleNewContent: () => void;
  restorePageScroll: () => void;
  setShowBrandVoiceOnParent: (show: boolean) => void;
  setShowContentFrameworkOnParent: (show: boolean) => void;
  setOpen: (open: boolean) => void;
  localPersonaSelection: Id<"personas"> | "none" | null;
  scrollbarWidth: number;
  siteId: Id<"sites">;
  id: string;
  projectData?: typeof dummyProjectData;
  aiOutput: string;
  readingLevelsData: Array<{
    label: string;
    value: string;
    description: string;
  }>;
  setSelectedPainPointId: (id: Id<"painPoints"> | null) => void;
}

// Simple dummy output section component
const DummyOutputSection = ({
  aiOutput,
  loading,
  isSaving,
  onSave
}: {
  aiOutput: string;
  loading: boolean;
  isSaving: boolean;
  onSave: () => void;
}) => {
  const [copied, setCopied] = useState(false);
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(aiOutput);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error("Failed to copy text: ", err);
    }
  };
  return <Card className="h-fit bg-zinc-900 text-white" data-sentry-element="Card" data-sentry-component="DummyOutputSection" data-sentry-source-file="dummy-template-content-generator.tsx">
      <CardHeader className="pb-3" data-sentry-element="CardHeader" data-sentry-source-file="dummy-template-content-generator.tsx">
        <div className="flex items-center justify-between gap-48">
          <div className="flex items-center gap-3">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-white/10">
              <Sparkles className="h-4 w-4 text-white" data-sentry-element="Sparkles" data-sentry-source-file="dummy-template-content-generator.tsx" />
            </div>
            <div>
              <h3 className="text-lg font-semibold">Generated Content</h3>
              <p className="text-sm text-default-500">
                AI-powered Twitter thread
              </p>
            </div>
          </div>
          <div className="flex gap-2">
            <Tooltip content={copied ? "Copied!" : "Copy to clipboard"} data-sentry-element="Tooltip" data-sentry-source-file="dummy-template-content-generator.tsx">
              <Button isIconOnly variant="light" size="sm" onClick={handleCopy} disabled={loading || !aiOutput} data-sentry-element="Button" data-sentry-source-file="dummy-template-content-generator.tsx">
                <Copy className="h-4 w-4 text-white" data-sentry-element="Copy" data-sentry-source-file="dummy-template-content-generator.tsx" />
              </Button>
            </Tooltip>
            {/* <Tooltip content="Download as text file">
              <Button
                isIconOnly
                variant="light"
                size="sm"
                disabled={loading || !aiOutput}
              >
                <Download className="h-4 w-4" />
              </Button>
             </Tooltip> */}
          </div>
        </div>
      </CardHeader>
      <CardBody className="bg-zinc-900 pt-0 text-white dark:bg-card" data-sentry-element="CardBody" data-sentry-source-file="dummy-template-content-generator.tsx">
        {loading ? <div className="space-y-3">
            <div className="flex items-center gap-2 text-sm text-default-500">
              <Sparkles className="h-4 w-4 animate-spin" />
              Generating your content...
            </div>
            <div className="space-y-2">
              <Skeleton className="h-4 w-full rounded-lg" />
              <Skeleton className="h-4 w-4/5 rounded-lg" />
              <Skeleton className="h-4 w-3/5 rounded-lg" />
              <Skeleton className="h-4 w-full rounded-lg" />
              <Skeleton className="h-4 w-2/3 rounded-lg" />
            </div>
          </div> : aiOutput ? <div className="space-y-4">
            <Textarea value={aiOutput} readOnly variant="bordered" minRows={12} maxRows={20} classNames={{
          base: "dark",
          input: "text-sm font-mono",
          inputWrapper: "bg-default-50"
        }} />
            <div className="flex gap-2">
              <Button color="primary" variant="solid" onClick={onSave} isLoading={isSaving} disabled={isSaving} startContent={!isSaving && <Save className="h-4 w-4" />} className="flex-1">
                {isSaving ? "Saving..." : "Save Content"}
              </Button>
              <Button variant="bordered" onClick={() => {}} className="flex-1">
                Generate New
              </Button>
            </div>
          </div> : <div className="flex flex-col items-center justify-center py-12 text-center">
            <Sparkles className="mb-4 h-12 w-12 text-default-300" />
            <h4 className="mb-2 text-lg font-medium text-default-600">
              Ready to Generate
            </h4>
            <p className="max-w-sm text-sm text-default-500">
              Fill out the form and click &quot;Generate Content&quot; to create
              your viral Twitter thread with AI.
            </p>
          </div>}
      </CardBody>
    </Card>;
};

// Simple dummy form component
const DummyFormSection = ({
  template,
  onGenerate,
  loading
}: {
  template: TEMPLATE;
  onGenerate: (data: any) => void;
  loading: boolean;
}) => {
  const [formData, setFormData] = useState<Record<string, string>>({});
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onGenerate(formData);
  };
  const handleInputChange = (name: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  return <Card className="h-fit bg-zinc-900 text-white" data-sentry-element="Card" data-sentry-component="DummyFormSection" data-sentry-source-file="dummy-template-content-generator.tsx">
      <CardHeader className="pb-3" data-sentry-element="CardHeader" data-sentry-source-file="dummy-template-content-generator.tsx">
        <div className="flex items-center gap-3">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-white/10">
            <span className="text-sm">🧵</span>
          </div>
          <div>
            <h3 className="text-lg font-semibold">{template.name}</h3>
            <p className="text-sm text-default-500">{template.desc}</p>
          </div>
        </div>
      </CardHeader>
      <CardBody className="pt-0" data-sentry-element="CardBody" data-sentry-source-file="dummy-template-content-generator.tsx">
        <form onSubmit={handleSubmit} className="space-y-4">
          {template.form.map(field => <div key={field.name}>
              {field.field === "input" ? <Input label={field.label} placeholder={field.placeholder} value={formData[field.name] || ""} onChange={e => handleInputChange(field.name, e.target.value)} required={field.required} labelPlacement="outside" variant="bordered" classNames={{
            base: "dark"
          }} className="text-white" /> : <Textarea label={field.label} placeholder={field.placeholder} value={formData[field.name] || ""} onChange={e => handleInputChange(field.name, e.target.value)} required={field.required} labelPlacement="outside" variant="bordered" minRows={3} classNames={{
            base: "dark"
          }} className="text-white" />}
            </div>)}

          <Button type="submit" color="primary" size="lg" className="w-full" isLoading={loading} disabled={loading} startContent={!loading && <Sparkles className="h-4 w-4" />} data-sentry-element="Button" data-sentry-source-file="dummy-template-content-generator.tsx">
            {loading ? "Generating..." : "Generate Content"}
          </Button>
        </form>
      </CardBody>
    </Card>;
};
const DummyTemplateContentGenerator = ({
  shouldShowBackButton = false,
  shouldShowExpandButton = false,
  isBothOptionsEnabled = false,
  showBrandVoiceOnParent = false,
  showContentFrameworkOnParent = false,
  predefinedBrandVoices = platformBrandVoices,
  customBrandVoices = dummyCustomBrandVoices,
  predefinedEmotions = platformEmotions,
  selectedEmotion = "AHA (💡 Educational)",
  personas = dummyPersonas,
  painPoints = dummyPainPoints,
  selectedPainPointId = dummyPainPoints[0]._id,
  selectedContentFramework = "aida",
  selectedBrandVoice = "professional-friendly",
  selectedReadingLevel = "high-school",
  selectedPronounStyle = "singular",
  handlePersonaChange = () => {},
  handleEmotionChange = async () => {},
  handleBrandVoiceChange = async (value: string | null) => {},
  handleReadingLevelChange = async () => {},
  handleWebsiteSummaryChange = async () => {},
  handlePronounStyleChange = async () => {},
  handleContentFrameworkChange = async () => {},
  GenerateAIContent = async () => {},
  loading = false,
  isSaving = false,
  handleSaveOutput = async () => {},
  handleNewContent = () => {},
  restorePageScroll = () => {},
  setShowBrandVoiceOnParent = () => {},
  setShowContentFrameworkOnParent = () => {},
  setOpen = () => {},
  localPersonaSelection = dummyPersonas[0]._id,
  scrollbarWidth = 0,
  siteId = "site1" as Id<"sites">,
  id = "project1",
  projectData = dummyProjectData,
  aiOutput = dummyAiOutput,
  readingLevelsData = dummyReadingLevels,
  setSelectedPainPointId = () => {}
}: Partial<DummyTemplateContentGeneratorProps> = {}) => {
  const isMounted = useIsMounted();
  const [isGenerating, setIsGenerating] = useState(false);

  // Local state for interactive selectors
  const [localSelectedPersona, setLocalSelectedPersona] = useState<Id<"personas"> | "none">(localPersonaSelection === null ? "none" : localPersonaSelection);
  const [localSelectedPainPoint, setLocalSelectedPainPoint] = useState<Id<"painPoints"> | null>(selectedPainPointId);

  // Demo function to simulate content generation
  const handleDemoGeneration = async (formData: any) => {
    setIsGenerating(true);
    // Simulate loading time
    setTimeout(() => {
      setIsGenerating(false);
    }, 2000);
  };
  return <div className="group relative h-full w-full" data-sentry-component="DummyTemplateContentGenerator" data-sentry-source-file="dummy-template-content-generator.tsx">
      {/* Left Fade Overlay */}
      <div className="pointer-events-none absolute inset-x-0 bottom-0 left-0 top-0 z-10 h-full w-20 bg-gradient-to-r from-black via-transparent to-transparent" />
      {/* Right Fade Overlay */}
      <div className="pointer-events-none absolute bottom-0 right-0 top-0 z-10 h-full w-20 bg-gradient-to-l from-black via-transparent to-transparent" />
      <div className="w-full min-w-[1000px] shrink-0 scale-80 rounded-xl border-l-0 border-t-1 border-zinc-900/80 bg-black/20 p-4 opacity-70 transition-all duration-500 group-hover:border-zinc-800 group-hover:opacity-100">
        {/* Top side glow */}
        {/* <GlowEffect
          colors={["#f97316", "#c2410c", "#fb923c", "#fca5a5", "#ef4444"]}
          sides={["top"]}
          mode="colorToTransparentLeftToRight"
          blur="soft"
          duration={4}
          className="absolute left-[0%] top-0 z-50 h-[2px] w-[100%] rounded-full opacity-100 transition-all duration-300 group-hover:h-[2px] group-hover:opacity-100 group-hover:duration-700"
         /> */}
        {/* Right side glow */}
        {/* <GlowEffect
          colors={["#f97316", "#c2410c", "#fb923c", "#fca5a5", "#ef4444"]}
          sides={["right"]}
          mode="colorToTransparentTopToBottom"
          blur="soft"
          duration={4}
          className="absolute left-0 top-[0%] h-[100%] w-[1px] rounded-full opacity-50 transition-all duration-300 group-hover:w-[2px] group-hover:opacity-100 group-hover:duration-700"
         /> */}
        <div className="w-full shrink-0 space-y-6 rounded-xl border-l-1 border-t-1 border-zinc-700 bg-zinc-900/80 p-4 transition-all duration-500 group-hover:border-zinc-500 group-hover:opacity-100">
          <div className="mb-6 flex flex-col gap-4 pr-28 sm:flex-row sm:items-center sm:justify-between">
            <AnimatePresence mode="wait" data-sentry-element="AnimatePresence" data-sentry-source-file="dummy-template-content-generator.tsx">
              {shouldShowBackButton ? <motion.div key="back-button" initial={{
              opacity: 0,
              x: -20
            }} animate={{
              opacity: 1,
              x: 0
            }} exit={{
              opacity: 0,
              x: -20
            }} transition={{
              duration: 0.2
            }} className="flex items-center gap-2">
                  <Button variant="light" size="sm" isDisabled>
                    <ArrowLeft className="mr-2 h-4 w-4" />
                    Back
                  </Button>
                  <Button variant="light" size="sm" isDisabled>
                    New
                  </Button>
                </motion.div> : isBothOptionsEnabled ? <motion.div key="minimize-button" initial={{
              opacity: 0
            }} animate={{
              opacity: 1
            }} exit={{
              opacity: 0
            }} transition={{
              duration: 0.2
            }}>
                  <Tooltip content="Disable Focus Mode">
                    <Button variant="light" size="sm" isIconOnly className="mt-2 opacity-40 hover:opacity-100" onClick={() => {
                  setShowBrandVoiceOnParent(false);
                  setShowContentFrameworkOnParent(false);
                  setOpen(true);
                }}>
                      <Minimize className="h-4 w-4" />
                    </Button>
                  </Tooltip>
                </motion.div> : <motion.div key="empty-space" initial={{
              opacity: 0
            }} animate={{
              opacity: 1
            }} exit={{
              opacity: 0
            }} transition={{
              duration: 0.2
            }} className="w-4"></motion.div>}
            </AnimatePresence>

            <div className="flex w-full flex-row justify-center gap-4 pr-5 sm:flex-row sm:items-center sm:justify-end">
              <AnimatePresence data-sentry-element="AnimatePresence" data-sentry-source-file="dummy-template-content-generator.tsx">
                {shouldShowExpandButton && <motion.div key="expand-button" initial={{
                opacity: 0
              }} animate={{
                opacity: 1
              }} exit={{
                opacity: 0
              }} transition={{
                duration: 0.2
              }}>
                    <Tooltip content="Enable Focus Mode">
                      <Button variant="light" size="sm" isIconOnly className="mr-2 mt-2 opacity-40 hover:opacity-100" onClick={() => {
                    setShowBrandVoiceOnParent(true);
                    setShowContentFrameworkOnParent(true);
                  }}>
                        <Expand className="h-4 w-4" />
                      </Button>
                    </Tooltip>
                  </motion.div>}
              </AnimatePresence>

              {/* Persona Dropdown */}
              <Select label="Select Target Persona" labelPlacement="outside" placeholder="Select a persona" selectedKeys={[localSelectedPersona]} onChange={e => {
              const newValue = e.target.value as Id<"personas"> | "none";
              setLocalSelectedPersona(newValue);
              handlePersonaChange(newValue);
              restorePageScroll();
            }} className="w-full max-w-[250px]" classNames={{
              base: "max-w-xs dark",
              popoverContent: "dark text-white",
              trigger: "h-12",
              label: "text-default-400"
            }} renderValue={items => {
              return items.map(item => {
                if (item.key === "none") {
                  return <div key={item.key} className="flex items-center gap-2">
                          <Avatar alt="No persona" className="flex-shrink-0" size="sm" src="/images/placeholder-avatar.png" />
                          <div className="flex flex-col">
                            <span>No persona</span>
                            <span className="line-clamp-1 text-tiny text-default-500">
                              Generate without persona context
                            </span>
                          </div>
                        </div>;
                }
                const persona = personas?.find(p => p._id === item.key);
                if (!persona) return null;
                return <div key={item.key} className="flex items-center gap-2">
                        <Avatar alt={persona.name} className="flex-shrink-0" size="sm" src={`https://api.dicebear.com/7.x/personas/svg?seed=${persona.name}`} />
                        <div className="flex flex-col">
                          <span>{persona.name}</span>
                          <span className="line-clamp-1 text-tiny text-default-500">
                            {persona.job_title}
                          </span>
                        </div>
                      </div>;
              });
            }} data-sentry-element="Select" data-sentry-source-file="dummy-template-content-generator.tsx">
                {/* Use Fragment to fix the collection type issue */}
                <React.Fragment>
                  <SelectItem key="none" textValue="No persona" className="border-b border-default-300/50" data-sentry-element="SelectItem" data-sentry-source-file="dummy-template-content-generator.tsx">
                    <div className="flex items-center gap-2">
                      <Avatar alt="No persona" className="flex-shrink-0" size="sm" src="/images/placeholder-avatar.png" data-sentry-element="Avatar" data-sentry-source-file="dummy-template-content-generator.tsx" />
                      <div className="flex flex-col">
                        <span className="text-small">No persona</span>
                        <span className="text-tiny text-default-400">
                          Universal content
                        </span>
                      </div>
                    </div>
                  </SelectItem>
                  {personas?.map(persona => <SelectItem key={persona._id} textValue={persona.name}>
                      <div className="flex items-center gap-2">
                        <Avatar alt={persona.name} className="flex-shrink-0" size="sm" src={`https://api.dicebear.com/7.x/personas/svg?seed=${persona.name}`} />
                        <div className="flex flex-col">
                          <span className="text-small">{persona.name}</span>
                          <span className="line-clamp-1 text-tiny text-default-500">
                            {persona.job_title}
                          </span>
                        </div>
                      </div>
                    </SelectItem>)}
                </React.Fragment>
              </Select>

              {/* Pain Points Dropdown */}
              <Autocomplete label="Select Pain Point" labelPlacement="outside" placeholder="Select or type pain point" selectedKey={localSelectedPainPoint || ""} onSelectionChange={key => {
              const newValue = key ? key as Id<"painPoints"> : null;
              setLocalSelectedPainPoint(newValue);
              setSelectedPainPointId(newValue);
              restorePageScroll();
            }} isClearable={true} className="w-full max-w-[250px]" classNames={{
              base: "max-w-xs dark",
              popoverContent: "w-[250px] dark text-white"
            }} size="md" variant="flat" inputProps={{
              classNames: {
                label: "text-default-400 text-small ",
                inputWrapper: "h-12 min-h-12"
              }
            }} defaultItems={painPoints?.map(painPoint => ({
              label: painPoint.shortName,
              value: painPoint._id,
              description: painPoint.fullDescription
            })) ?? []} data-sentry-element="Autocomplete" data-sentry-source-file="dummy-template-content-generator.tsx">
                {(item: any) => <AutocompleteItem key={item.value} textValue={item.label} className="data-[hover=true]:bg-default-100">
                    <div className="flex flex-col">
                      <span className="text-small">{item.label}</span>
                      <span className="line-clamp-2 text-tiny text-default-400">
                        {item.description}
                      </span>
                    </div>
                  </AutocompleteItem>}
              </Autocomplete>

              {/* Target Emotion */}
              <EmotionSelector selectedEmotion={selectedEmotion} onEmotionChange={handleEmotionChange} predefinedEmotions={predefinedEmotions} isCompact={true} siteId={siteId} className="w-full max-w-[250px]" passedClassNames={{
              base: "dark",
              popoverContent: "dark text-white"
            }} data-sentry-element="EmotionSelector" data-sentry-source-file="dummy-template-content-generator.tsx" />

              <AnimatePresence data-sentry-element="AnimatePresence" data-sentry-source-file="dummy-template-content-generator.tsx">
                {showContentFrameworkOnParent && <motion.div key="content-framework" className="flex w-full max-w-[250px] flex-col gap-2" initial={{
                opacity: 0,
                height: 0,
                width: 0
              }} animate={{
                opacity: 1,
                height: "auto",
                width: "auto"
              }} exit={{
                opacity: 0,
                height: 0,
                width: 0
              }} transition={{
                duration: 0.4
              }}>
                    <Autocomplete label="Content Framework" placeholder="Select framework" labelPlacement="outside" selectedKey={selectedContentFramework || ""} onSelectionChange={key => {
                  if (typeof key === "string") {
                    handleContentFrameworkChange(key);
                    restorePageScroll();
                  }
                }} defaultItems={contentFrameworks} className="w-full max-w-[250px]" classNames={{
                  base: "max-w-xs dark",
                  popoverContent: "w-[250px] dark text-white"
                }} size="md" variant="flat" inputProps={{
                  classNames: {
                    label: "text-default-400 text-small ",
                    inputWrapper: "h-12 min-h-12"
                  }
                }}>
                      {(item: any) => <AutocompleteItem key={item.value} textValue={item.label} className="data-[hover=true]:bg-default-100">
                          <div className="flex flex-col">
                            <span className="text-small">{item.label}</span>
                            <span className="line-clamp-2 text-tiny text-default-400">
                              {item.description}
                            </span>
                          </div>
                        </AutocompleteItem>}
                    </Autocomplete>
                  </motion.div>}
              </AnimatePresence>

              <AnimatePresence data-sentry-element="AnimatePresence" data-sentry-source-file="dummy-template-content-generator.tsx">
                {showBrandVoiceOnParent && <motion.div key="brand-voice" className="flex w-full max-w-[250px] flex-col gap-2" initial={{
                opacity: 0,
                height: 0,
                width: 0
              }} animate={{
                opacity: 1,
                height: "auto",
                width: "auto"
              }} exit={{
                opacity: 0,
                height: 0,
                width: 0
              }} transition={{
                duration: 0.4
              }}>
                    <BrandVoiceSelector selectedBrandVoice={selectedBrandVoice} onBrandVoiceChange={value => {
                  handleBrandVoiceChange(value);
                }} platformVoices={predefinedBrandVoices} customVoices={customBrandVoices} isCompact={true} siteId={siteId} className="w-full dark" passedClassNames={{
                  base: "dark",
                  popoverContent: "dark text-white"
                }} />
                  </motion.div>}
              </AnimatePresence>
            </div>
          </div>

          {/* Responsive grid layout */}
          <div className="grid grid-cols-1 gap-5 md:grid-cols-3">
            {/* Dummy FormSection */}
            <DummyFormSection template={dummyTemplate} onGenerate={handleDemoGeneration} loading={isGenerating} data-sentry-element="DummyFormSection" data-sentry-source-file="dummy-template-content-generator.tsx" />
            {/* Dummy OutputSection */}
            <div className="col-span-1 md:col-span-2">
              <DummyOutputSection aiOutput={aiOutput} loading={isGenerating} isSaving={isSaving} onSave={handleSaveOutput} data-sentry-element="DummyOutputSection" data-sentry-source-file="dummy-template-content-generator.tsx" />
            </div>
          </div>
        </div>
      </div>
    </div>;
};
export default DummyTemplateContentGenerator;